import { msg } from "@lingui/core/macro";
import { createRoute, Link, useParams } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { createSignal, For, Show } from "solid-js";
import { AuthPlaceholder } from "@/components/auth-placeholder";
import { PageContainer } from "@/components/ui/page-container";
import { ArrowLeft, Loader2, Mail, UserPlus, Users } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TextField } from "@/components/ui/text-field";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { Separator } from "@/components/ui/separator";
import { useOrganizationMembers } from "@/features/organizations/hooks/use-organization-members";
import { api, createApiQuery } from "@/lib/api";
import { type components, queryKeys } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { OrganizationMemberRow } from "@/features/organizations/components/organization-member-row";

function OrganizationDetailPage() {
  const { t } = useI18n();
  const params = useParams({ from: "/_app/organizations/$orgId" });
  const [email, setEmail] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const org = () => orgs.data?.find((o) => o.id === params().orgId);

  const { members, isLoading, inviteMember, removeMember } = useOrganizationMembers(
    () => params().orgId,
  );

  const handleInvite = async (e: Event) => {
    e.preventDefault();
    const mail = email().trim();
    if (!mail) {
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteMember(mail, "member");
      setEmail("");
      showToast(t(msg`Invitation sent`), "success");
    } catch (err) {
      console.error("Error inviting member:", err);
      showToast(t(msg`Error sending invitation`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await removeMember(memberId);
      showToast(t(msg`Member removed`), "success");
    } catch (err) {
      console.error("Error removing member:", err);
      showToast(t(msg`Error removing member`), "error");
    }
  };

  return (
    <AuthPlaceholder
      title={t(msg`Organization Detail`)}
      description={t(msg`Sign in to view organization details and collaborate with members.`)}
    >
      <PageContainer maxWidth="5xl" class="space-y-8">
        <Show
          when={!orgs.isLoading}
          fallback={
            <div class="space-y-8">
              <div class="flex items-center gap-4">
                <Skeleton class="h-8 w-8 rounded-md" />
                <Skeleton class="h-8 w-48" />
              </div>
              <div class="grid gap-6 md:grid-cols-2">
                <Skeleton class="h-64 w-full rounded-xl" />
                <Skeleton class="h-64 w-full rounded-xl" />
              </div>
            </div>
          }
        >
          <Show
            when={org()}
            fallback={
              <div class="flex flex-col items-center justify-center py-20 text-center space-y-4">
                <div class="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
                  <Users class="h-10 w-10 text-muted-foreground" />
                </div>
                <h1 class="text-2xl font-bold">{t(msg`Organization not found`)}</h1>
                <Link to="/organizations">
                  <Button variant="outline">{t(msg`Back to Organizations`)}</Button>
                </Link>
              </div>
            }
          >
            {(o) => (
              <>
                <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div class="space-y-1">
                    <div class="flex items-center gap-3">
                      <Link to="/organizations">
                        <Button variant="ghost" size="icon" class="h-8 w-8">
                          <ArrowLeft class="h-4 w-4" />
                        </Button>
                      </Link>
                      <h1 class="text-3xl font-bold tracking-tight">{o().name}</h1>
                    </div>
                    <div class="flex items-center gap-2 pl-11">
                      <Badge variant="secondary" class="text-[10px] uppercase tracking-wider">
                        @{o().slug}
                      </Badge>
                      <span class="text-xs text-muted-foreground">
                        {(() => {
                          const id = o().id;
                          return t(msg`ID: ${id}`);
                        })()}
                      </span>
                    </div>
                  </div>
                </header>

                <div class="grid gap-6 md:grid-cols-2">
                  {/* Miembros */}
                  <Card>
                    <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle class="text-lg font-bold">{t(msg`Members`)}</CardTitle>
                      <Users class="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div class="space-y-4">
                        <For
                          each={members()}
                          fallback={
                            <Show when={!isLoading()}>
                              <div class="py-10 text-center text-sm text-muted-foreground">
                                {t(msg`No members yet`)}
                              </div>
                            </Show>
                          }
                        >
                          {(member) => (
                            <OrganizationMemberRow member={member} onRemove={handleRemove} />
                          )}
                        </For>
                        <Show when={isLoading() && members().length === 0}>
                          <For each={Array.from({ length: 3 })}>
                            {() => <Skeleton class="h-12 w-full rounded-md" />}
                          </For>
                        </Show>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Invitaciones / Acciones */}
                  <Card>
                    <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle class="text-lg font-bold">{t(msg`Invite members`)}</CardTitle>
                      <Mail class="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent class="space-y-6">
                      <form onSubmit={handleInvite} class="space-y-4">
                        <div class="space-y-2">
                          <TextField
                            type="email"
                            placeholder={t(msg`email@example.com`)}
                            value={email()}
                            onInput={(e) => setEmail(e.currentTarget.value)}
                            disabled={isSubmitting()}
                          />
                        </div>
                        <Button
                          type="submit"
                          class="w-full gap-2"
                          disabled={isSubmitting() || !email().includes("@")}
                        >
                          <Show
                            when={isSubmitting()}
                            fallback={
                              <>
                                <UserPlus class="h-4 w-4" /> {t(msg`Invite`)}
                              </>
                            }
                          >
                            <Loader2 class="h-4 w-4 animate-spin" /> {t(msg`Inviting...`)}
                          </Show>
                        </Button>
                      </form>

                      <Separator />

                      <div class="space-y-2 text-center">
                        <p class="text-xs text-muted-foreground leading-relaxed">
                          {t(msg`Invited members will receive an email to join this organization.`)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </Show>
        </Show>
      </PageContainer>
    </AuthPlaceholder>
  );
}

export const organizationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organizations/$orgId",
  component: OrganizationDetailPage,
});
